/**
 * Physics Config
 *
 * Client-owned movement physics tuning shared between client simulation
 * and server validation. Touch these only when chasing a specific feel issue.
 *
 * Two-section file (defaults+dev-override pattern):
 *   1. PHYSICS_DEFAULTS — immutable production values.
 *   2. PHYSICS_DEV_OVERRIDE — Partial<>; tweak here while iterating, leave
 *      `{}` for shipped builds.
 *
 * Consumers import `PHYSICS` (the merged result) — never the raw defaults
 * or override slice — so the merge happens in one canonical place.
 *
 * Pattern docs: `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface PhysicsConfig {
  gravity: {
    /** Pixels per second² applied while airborne. */
    default: number;
    /**
     * Multiplier applied while falling (vy > 0). 1.0 = symmetric arc.
     * Was 1.8 (asymmetric, snappier feel) but caused apex-snap; pinned to
     * 1.0 since the netsync rework.
     */
    fallingMultiplier: number;
    /** Hard cap on vy (pixels/sec) so a fall never escalates past this. */
    terminalVelocity: number;
    /** Per-frame velocity damping while airborne. 0 = stop, 1 = no damping. */
    airFriction: number;
    /** Absolute max fall speed (pixels/sec). Wall against runaway accumulation. */
    maxFallSpeed: number;
    /** NPC-only gravity (typically lighter than player). */
    npcGravity: number;
  };
  friction: {
    /** Air friction (vx damping while airborne). */
    air: number;
    /** Ground deceleration (pixels/sec²) when no input. */
    groundDecel: number;
    /** NPC ground drag. */
    npcDrag: number;
  };
  movement: {
    /** Milliseconds after leaving a ledge during which jump still counts. */
    coyoteTimeMs: number;
    /** Milliseconds before landing during which a jump press still queues. */
    jumpBufferMs: number;
    /** Pixels of horizontal nudge that lets you slip past a wall corner. */
    cornerNudgePx: number;
  };
  logic: {
    groundSnapOffsetY: number;
    groundSnapBuffer: number;
    maxPlatformSearchDistance: number;
    platformCheckIntervalMs: number;
  };
  simulation: {
    /** Fixed physics tick (seconds). 1/60 = 60 Hz. */
    fixedDt: number;
    /** Cap on accumulated time per frame (seconds) — prevents spiral-of-death. */
    maxAccumulator: number;
  };
}

export const PHYSICS_DEFAULTS: PhysicsConfig = {
  gravity: {
    default: 1500,
    fallingMultiplier: 1.0,
    terminalVelocity: 1200,
    airFriction: 0.98,
    maxFallSpeed: 2000,
    npcGravity: 1000,
  },
  friction: {
    air: 0.98,
    groundDecel: 2000,
    npcDrag: 500,
  },
  movement: {
    coyoteTimeMs: 100,
    jumpBufferMs: 100,
    cornerNudgePx: 12,
  },
  logic: {
    groundSnapOffsetY: -40,
    groundSnapBuffer: 20,
    maxPlatformSearchDistance: 500,
    platformCheckIntervalMs: 100,
  },
  simulation: {
    fixedDt: 1 / 60,
    maxAccumulator: 0.1,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to PHYSICS_DEFAULTS. Use when iterating on game feel; promote to defaults
 * once happy.
 *
 * Examples:
 *   gravity: { default: 1200, terminalVelocity: 1000 }   // floatier feel
 */
export const PHYSICS_DEV_OVERRIDE: Partial<PhysicsConfig> = {};

/**
 * Merge defaults with dev override one section at a time so an override of
 * `{ gravity: { default: 1200 } }` correctly merges with the defaults'
 * gravity bucket (instead of replacing it whole).
 */
export const PHYSICS: PhysicsConfig = {
  gravity: { ...PHYSICS_DEFAULTS.gravity, ...PHYSICS_DEV_OVERRIDE.gravity },
  friction: { ...PHYSICS_DEFAULTS.friction, ...PHYSICS_DEV_OVERRIDE.friction },
  movement: { ...PHYSICS_DEFAULTS.movement, ...PHYSICS_DEV_OVERRIDE.movement },
  logic: { ...PHYSICS_DEFAULTS.logic, ...PHYSICS_DEV_OVERRIDE.logic },
  simulation: { ...PHYSICS_DEFAULTS.simulation, ...PHYSICS_DEV_OVERRIDE.simulation },
};
